// ═══════════════════════════════════════════════════════════
// DropdownMenu — portal-based dropdown that escapes overflow
//
// Renders the menu at <body> using position:fixed so it is
// never clipped by overflow:hidden ancestors or card borders.
// Automatically flips upward if it would overflow the viewport.
// ═══════════════════════════════════════════════════════════

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface DropdownMenuProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  open: boolean;
  onClose: () => void;
  /** Pin menu's left edge to trigger's left, or right edge to trigger's right (default: right) */
  align?: 'left' | 'right';
  minWidth?: number;
  /** Set to true when the trigger should fill its container width */
  fullWidth?: boolean;
}

export function DropdownMenu({
  trigger, children, open, onClose, align = 'right', minWidth, fullWidth,
}: DropdownMenuProps) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef    = useRef<HTMLDivElement>(null);
  // null = not yet positioned; hide until first layout pass
  const [style, setStyle] = useState<React.CSSProperties | null>(null);

  // useLayoutEffect fires synchronously after DOM paint — no flash
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) { setStyle(null); return; }
    const r  = triggerRef.current.getBoundingClientRect();
    const mh = menuRef.current?.offsetHeight ?? 240;
    const spaceBelow = window.innerHeight - r.bottom - 8;
    const top = spaceBelow >= mh ? r.bottom + 4 : r.top - mh - 4;

    setStyle(
      align === 'right'
        ? { top, right: window.innerWidth - r.right }
        : { top, left: r.left },
    );
  }, [open, align]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      onClose();
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      <div ref={triggerRef} style={fullWidth ? { display: 'block', width: '100%' } : { display: 'inline-flex' }}>
        {trigger}
      </div>
      {open && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            // hidden until positioned to avoid the 0,0 flash
            visibility: style ? 'visible' : 'hidden',
            ...style,
            zIndex: 9999,
            background: 'white',
            border: '1px solid var(--color-neutral-200)',
            borderRadius: 4,
            boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
            minWidth: minWidth ?? 160,
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  );
}
